// UrlField — canonical `url` field primitive (DRIFT-910 slice 1, CHG-2457).
// Single-line URL input (`<input type="url">`); a TextField sibling that opts
// into native URL semantics (mobile URL keyboard, browser format hinting).
// Storage: string; emits `null` when cleared. Server still re-validates the URL.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('url') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the input via FieldShell <label htmlFor=id>;
// validation error in a role="alert" live region; aria-invalid + aria-required
// on the input; aria-describedby links the input to its error + helper text nodes.

import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

export interface UrlFieldProps extends FieldPrimitiveProps<string> {
  placeholder?: string;
  maxLength?: number;
}

export function UrlField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
  placeholder,
  maxLength,
}: UrlFieldProps) {
  const ids = useFieldIds();
  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      <input
        id={ids.inputId}
        name={name}
        type="url"
        inputMode="url"
        value={value ?? ""}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(ids, error, helperText)}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={fieldControlStyle(Boolean(error))}
      />
    </FieldShell>
  );
}
